import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Leaf, GraduationCap } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import { Spinner } from "@/components/common/Primitives";
import { useForums, useUnitCount } from "@/hooks/useSiteQueries";
import { withUnitCountInText } from "@/lib/unitCountDisplay";

const FORUM_ICONS = { Landmark, Leaf, GraduationCap };

export default function OurWorkPage() {
  const { data: forums, isPending } = useForums();
  const { data: unitCount } = useUnitCount();

  return (
    <div data-testid="our-work-page">
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-28">
          <p className="eyebrow !text-gold-light mb-4">Forums</p>
          <h1 className="heading-xl !text-white max-w-3xl">
            Three forums, <span className="italic text-gold-light">one mission</span> across the Archdiocese
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            {withUnitCountInText(
              "The Sabha's work is organised through three dedicated forums that serve civic life, the environment, and the next generation of leaders — active in every deanery through our 48+ units.",
              unitCount
            )}
          </p>
        </div>
      </section>

      <section className="container-bcs py-20 sm:py-24">
        {!forums || isPending ? (
          <Spinner />
        ) : (
          <div className="space-y-10">
            {forums.map((forum, i) => {
              const Icon = FORUM_ICONS[forum.icon] || Landmark;
              return (
                <div
                  key={forum.slug}
                  className={`card-bcs overflow-hidden grid grid-cols-1 lg:grid-cols-2 ${i % 2 ? "lg:[direction:rtl]" : ""}`}
                  data-testid={`work-forum-${forum.slug}`}
                >
                  <div className="h-64 lg:h-auto [direction:ltr]">
                    <img src={forum.hero_image} alt={forum.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-8 sm:p-10 [direction:ltr]">
                    <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon">
                      <Icon size={20} />
                    </span>
                    <h2 className="mt-5 heading-md">{forum.name}</h2>
                    <p className="mt-1 text-sm font-medium text-gold-dark italic font-serif text-base">{forum.tagline}</p>
                    <p className="mt-4 text-sm sm:text-base text-ink-soft leading-relaxed">{forum.description}</p>
                    <ul className="mt-5 space-y-2">
                      {(forum.focus_areas || []).slice(0, 4).map((f, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-sm text-ink-soft">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link to={`/our-work/${forum.slug}`} className="btn-primary mt-7" data-testid={`visit-forum-${forum.slug}`}>
                      Visit forum <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-cream-alt border-t border-cream-dark">
        <div className="container-bcs py-16 text-center">
          <SectionHeading
            align="center"
            eyebrow="Get Involved"
            title="Volunteer with a forum"
            subtitle="Each forum welcomes volunteers from every unit — register your interest and we will connect you."
          />
          <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="work-volunteer-btn">
            Register as a volunteer <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
